// Import react elements
import { useCallback, useContext, useState } from 'react';

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import strich api calls
import {
    getPodDesigns,
    addNewImage,
    getPrintAreaRoles,
} from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";
import { formatDisplayDateTime } from 'global/utilities/ConvertDate';



// Custom hook to manage fetching, filtering, updating, and uploading images
const usePodProductData = () => {


    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);

    // Define toast options
    const { toastError } = useToast();

    // Set loading state - while api call is fetching pod products
    const [loadingPodDesigns, setLoadingPodDesigns] = useState(true);

    // Set state for holding the pod designs
    const [podDesignsData, setPodDesignsData] = useState([]);

    // Set state for holding all the existing categories for each language - for dropdown selection 
    const [existingCategories, setExistingCategories] = useState({});



    // Function to get all the pod designs - use useCallback to create a stable fetchPodDesigns function that depends on token and authReady
    const fetchPodDesigns = useCallback(async (type) => {

        // Check if there's no token or the authReady flag is false
        if (!authReady || !token) {

            // Exit early
            return;
        
        };

        // Set loading to true
        setLoadingPodDesigns(true);

        // Get the roles
        const allRolesResponse = await getPrintAreaRoles(token);

        const allRoles = allRolesResponse.success ? allRolesResponse.results : null;


        // Get api response
        const response = await getPodDesigns(token, type);

        // Store the templates used to resolve template names for design cards
        let availablePodTemplates = [];

        // Designs can be linked to templates, so get the template headers as well
        if (type === "design") {

            // Get the available template headers
            const templatesResponse = await getPodDesigns(token, "template");

            // Keep valid template results without preventing designs loading if this call fails
            if (templatesResponse.success && Array.isArray(templatesResponse.results)) {
                availablePodTemplates = templatesResponse.results;
            }

        }

        // Check if it is successful and that the results is an array
        if (response.success && Array.isArray(response.results)) {

            // It is so map the designs returned
            const mappedPodDesigns = response.results.map((podDesign) => {

                // Find the matching connection to do a look up for the connection name
                //const matchingStoreID = connectionsData.find(connection => connection.id === podDesign.connid);

                // Find the english translations (can change this to our 'chosen language' later)
                const englishTranslations = podDesign.translations.find(translations => translations.language === 'EN');

                // Get the images for each role in the design set
                const designSetImages = podDesign.roles.map((role) => (

                    <div key={role.role}>
                        <div key={role.role} className='pod-designs-table_design'>
                            <img src={role.thumbnail_url} alt={role.role} />
                        </div>
                        <span className="pod-designs__roleTitle">{allRoles ? allRoles?.find((r) => r.role === role.role).name : ""}</span>
                    </div>

                ) );

                const numberOfRoles = podDesign.roles.length;

                // Start without template names for designs which do not use templates
                const templateNames = [];

                // Resolve every assigned template name for this design
                if (type === "design" && Array.isArray(podDesign.template_ids)) {

                    // Go through each assigned template id
                    podDesign.template_ids.forEach((templateId) => {

                        // Find the template assigned to this design
                        const matchingTemplate = availablePodTemplates.find((template) => String(template.id) === String(templateId));

                        // Skip assignments whose template header is unavailable
                        if (!matchingTemplate) { return; }

                        // Find the English template translation
                        const templateEnglishTranslation = Array.isArray(matchingTemplate.translations)
                            ? matchingTemplate.translations.find((translation) => translation.language === "EN")
                            : null;

                        // Add the best available readable template name
                        templateNames.push(
                            templateEnglishTranslation?.title ||
                            matchingTemplate.title ||
                            matchingTemplate.code ||
                            "Template " + matchingTemplate.id
                        );

                    });

                }

                const titleWithPill = type === "template" && podDesign.source === "strich" ? 
                
                    <>
                        <div className="pod-designs-table_design--titleWithPill">
                            {englishTranslations?.title || ""}
                        </div>
                        <div className="pod-pricing-page__plan-badge pod-designs-table_design--titlePill">
                            Strich
                        </div>

                    </> 
                    
                    : englishTranslations?.title || "";

                
                // Create an array with formatted data that we want to display in the table
                return {

                    ...podDesign,

                    preview_image_url:
                    
                        <div key={podDesign.id} className="pod-designs-table_design-set">
                            {designSetImages}
                        </div> || "",

                    titleWithPill: titleWithPill,
                    title: englishTranslations?.title || "",
                    templateNames: templateNames.join(", "),
                    description: englishTranslations?.description || "",
                    keywords: englishTranslations?.keywords || "",
                    category: englishTranslations?.category || "",
                    subcategory: englishTranslations?.subcategory || "",
                    numberOfRoles: numberOfRoles,
                    created: formatDisplayDateTime(podDesign.created)

                };

                // Sort the array by id newest to oldest
            }).sort((a, b) => b.id - a.id);

            // Update pod designs data with the mapped response results
            setPodDesignsData(mappedPodDesigns);

            // Group the categories and subcategories by language 
            const groupedByLanguage = {};

            // For each pod design returned
            response.results.forEach(podDesign => {

                // For each translation language
                (podDesign.translations || []).forEach(translation => {

                    // Set the language, category and sub category (default category to '' if missing)
                    const lang = translation.language;
                    const category = translation.category?.trim() || '';
                    const subCategory = translation.subcategory?.trim();

                    // Skip if subcategory is missing
                    //if (!subCategory) return;

                    // If the language isn't already added then add it
                    if (!groupedByLanguage[lang]) {
                        groupedByLanguage[lang] = {};
                    }

                    // If the category for the language isn't already added then add it
                    if (!groupedByLanguage[lang][category]) {
                        groupedByLanguage[lang][category] = [];
                    }

                    // If the sub category for the language and category isn't already added then add it
                    if (!groupedByLanguage[lang][category].includes(subCategory)) {
                        groupedByLanguage[lang][category].push(subCategory);
                    }
                });
            });

            // Sort all categories and subcategories alphabetically for each language
            for (const lang in groupedByLanguage) {

                const sortedCategoryEntries = Object.entries(groupedByLanguage[lang])
                    .filter(([category]) => category.trim() !== '')
                    .map(([category, subcats]) => [category, subcats.sort((a, b) => a.localeCompare(b))])
                    .sort((a, b) => a[0].localeCompare(b[0]));

                // Rebuild object with sorted keys
                groupedByLanguage[lang] = Object.fromEntries(sortedCategoryEntries);

            }

            // Set existing categories so we can use it in the pod design details pop up
            setExistingCategories(groupedByLanguage);

        } else {

            // Create error message
            const errorMessage = response.error_message ? response.error_message : "Please try again."

            // Toast error message
            toastError({ title: "Error", message: "Could not get POD " + type +"s: " + errorMessage, autoCloseMs: 6000 });

        }

        // Set loading state to false once the API call is complete
        setLoadingPodDesigns(false);
       

        // Dependencies that trigger a new instance of this function
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, token]);



    // Function to add an image to the image bucket
    const addImageToImageBucket = async (payload) => {

        // Set thumbnail size so one is created (we use it in the pod designs table)
        const thumbnailSize = 300;

        // Make api call (include the thumbnail size)
        const response = await addNewImage(token, payload, thumbnailSize);

        return response;

    };



    return {

        fetchPodDesigns,
        loadingPodDesigns,
        podDesignsData,
        setPodDesignsData,
        existingCategories, 
        addImageToImageBucket,

    };

};

export default usePodProductData;